import { SelectHTMLAttributes, forwardRef, ReactNode, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { smoothTransition, shakeVariants } from "@/lib/animations";
import { useReducedMotion } from "@/hooks/useReducedMotion";

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  options?: { value: string; label: string }[];
  children?: ReactNode;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, error, options, children, className, onFocus, onBlur, ...props }, ref) => {
    const [isFocused, setIsFocused] = useState(false);
    const prefersReducedMotion = useReducedMotion();

    const handleFocus = (e: React.FocusEvent<HTMLSelectElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLSelectElement>) => {
      setIsFocused(false);
      onBlur?.(e);
    };

    return (
      <motion.div
        className="w-full"
        animate={error && !prefersReducedMotion ? "shake" : undefined}
        variants={shakeVariants}
      >
        {label && (
          <label
            htmlFor={props.id}
            className={cn(
              "block text-[13px] font-medium mb-1.5 transition-colors duration-150",
              error
                ? "text-(--color-error)"
                : isFocused
                  ? "text-[var(--color-primary)]"
                  : "text-(--color-text-secondary)"
            )}
          >
            {label}
            {props.required && <span className="text-(--color-error) ml-0.5">*</span>}
          </label>
        )}
        <motion.div
          className="relative"
          animate={
            prefersReducedMotion
              ? {}
              : {
                  scale: isFocused ? 1.005 : 1,
                }
          }
          transition={smoothTransition}
        >
          <select
            ref={ref}
            onFocus={handleFocus}
            onBlur={handleBlur}
            className={cn(
              "w-full h-[44px] px-4 text-[14px] appearance-none",
              "bg-(--color-surface-primary) border rounded-lg",
              "text-(--color-text-primary)",
              "transition-all duration-150 cursor-pointer",
              "focus:outline-none focus:ring-2 focus:ring-offset-0",
              "disabled:bg-[var(--color-bg-secondary)] disabled:text-(--color-text-muted) disabled:cursor-not-allowed",
              error
                ? "border-[var(--color-error)] focus:ring-[var(--color-error)]/20 focus:border-[var(--color-error)]"
                : "border-[var(--color-surface-border)] focus:ring-[var(--color-primary)]/20 focus:border-[var(--color-primary)]",
              "pr-10",
              className
            )}
            {...props}
          >
            {children ? (
              children
            ) : (
              <>
                <option value="">Select {label?.toLowerCase() || "option"}</option>
                {options?.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </>
            )}
          </select>
          <motion.div
            className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3"
            animate={
              prefersReducedMotion
                ? {}
                : {
                    rotate: isFocused ? 180 : 0,
                  }
            }
            transition={smoothTransition}
          >
            <svg
              className={cn(
                "h-5 w-5 transition-colors duration-150",
                isFocused ? "text-[var(--color-primary)]" : "text-(--color-text-muted)"
              )}
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                clipRule="evenodd"
              />
            </svg>
          </motion.div>
        </motion.div>
        <AnimatePresence mode="wait">
          {error && (
            <motion.p
              initial={{ opacity: 0, y: -5, height: 0 }}
              animate={{ opacity: 1, y: 0, height: "auto" }}
              exit={{ opacity: 0, y: -5, height: 0 }}
              transition={smoothTransition}
              className="mt-1.5 text-[13px] text-(--color-error) flex items-center gap-1"
            >
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              {error}
            </motion.p>
          )}
        </AnimatePresence>
      </motion.div>
    );
  }
);

Select.displayName = "Select";
